import React, { useState } from 'react';
import { Plus, Search, Mail, Phone, MapPin, User, Save, X, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSuppliers, useCreateSupplier, useUpdateSupplier, useDeleteSupplier } from '../hooks/usePurchases';
import { useAuthToken } from '../../../lib/useAuthToken';

const SuppliersPage: React.FC = () => {
  const t = useAuthToken();
  const { data: suppliers, isLoading } = useSuppliers();
  const createMut = useCreateSupplier();
  const updateMut = useUpdateSupplier();
  const deleteMut = useDeleteSupplier();

  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<any>(null);
  const [isNew, setIsNew] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<any>(null);

  // Form state
  const [form, setForm] = useState({
    name: '', contact_name: '', email: '', phone: '', city: '', address: '', nit: ''
  });

  const reset = () => {
    setForm({ name: '', contact_name: '', email: '', phone: '', city: '', address: '', nit: '' });
    setEditing(null);
    setIsNew(false);
  };

  const handleEdit = (s: any) => {
    setEditing(s);
    setForm({
      name: s.name || '',
      contact_name: s.contact_name || '',
      email: s.email || '',
      phone: s.phone || '',
      city: s.city || '',
      address: s.address || '',
      nit: s.nit || ''
    });
    setIsNew(false);
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('El nombre es obligatorio');
    try {
      if (isNew) {
        await createMut.mutateAsync(form);
        toast.success('Proveedor creado');
      } else {
        await updateMut.mutateAsync({ id: editing.id, data: form });
        toast.success('Proveedor actualizado');
      }
      reset();
    } catch (e) {
      toast.error('Error al guardar');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    try {
      await deleteMut.mutateAsync(confirmDelete.id);
      toast.success('Proveedor eliminado');
      setConfirmDelete(null);
    } catch (e) {
      toast.error('Error al eliminar el proveedor');
    }
  };

  const filtered = (suppliers ?? []).filter(s =>
    s.name.toLowerCase().includes(q.toLowerCase()) ||
    (s.contact_name?.toLowerCase()?.includes(q.toLowerCase()))
  );

  if (isLoading) return <div className="p-8 text-center text-muted">Cargando proveedores...</div>;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-5xl space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted">Compras</p>
            <h1 className="text-3xl font-bold text-[color:var(--color-text)] flex items-center gap-2">
              <User size={28} className="text-primary" /> Proveedores
            </h1>
            <p className="mt-1 text-sm text-muted">Gestiona tus contactos comerciales y proveedores de insumos</p>
          </div>
          <button
            onClick={() => { reset(); setIsNew(true); }}
            className="inline-flex h-11 items-center gap-2 rounded-full bg-primary px-6 text-sm font-bold text-white shadow hover:opacity-90 transition"
          >
            <Plus size={18} /> Nuevo Proveedor
          </button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Buscar por nombre o contacto..."
            className="w-full rounded-2xl border border-divider bg-surface px-11 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
          />
        </div>

        {/* Create / Edit Form */}
        {(isNew || editing) && (
          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[color:var(--color-text)]">
                {isNew ? 'Crear Nuevo Proveedor' : `Editando: ${editing.name}`}
              </h2>
              <button onClick={reset} className="text-muted hover:text-red-500 transition"><X size={20}/></button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { label: 'Nombre / Razón Social *', key: 'name' },
                { label: 'NIT / Identificación', key: 'nit' },
                { label: 'Persona de Contacto', key: 'contact_name' },
                { label: 'Teléfono', key: 'phone' },
                { label: 'Correo Electrónico', key: 'email' },
                { label: 'Ciudad', key: 'city' },
              ].map(f => (
                <div key={f.key}>
                  <label className="mb-1 block text-xs font-semibold text-muted">{f.label}</label>
                  <input
                    type="text"
                    value={(form as any)[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              ))}
              <div className="sm:col-span-2 lg:col-span-3">
                <label className="mb-1 block text-xs font-semibold text-muted">Dirección</label>
                <input
                  type="text"
                  value={form.address}
                  onChange={e => setForm({ ...form, address: e.target.value })}
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={reset} className="px-5 py-2 text-sm font-semibold text-muted hover:text-[color:var(--color-text)]">Cancelar</button>
              <button
                onClick={handleSave}
                disabled={createMut.isPending || updateMut.isPending}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-2 text-sm font-bold text-white shadow-lg hover:opacity-90 disabled:opacity-60 transition"
              >
                <Save size={16} /> {createMut.isPending || updateMut.isPending ? 'Guardando...' : 'Guardar Proveedor'}
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {confirmDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="mx-4 w-full max-w-sm rounded-2xl bg-surface p-6 shadow-2xl border border-divider">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <Trash2 size={22} className="text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-[color:var(--color-text)]">Eliminar proveedor</h3>
              <p className="mt-2 text-sm text-muted">
                ¿Estás seguro que deseas eliminar a <span className="font-semibold text-[color:var(--color-text)]">{confirmDelete.name}</span>?
                {confirmDelete.purchaseOrders?.length > 0 && (
                  <span className="block mt-1 text-amber-600 font-medium">Este proveedor tiene órdenes de compra asociadas y solo será desactivado.</span>
                )}
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="px-5 py-2 text-sm font-semibold text-muted hover:text-[color:var(--color-text)] transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleteMut.isPending}
                  className="inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60 transition"
                >
                  <Trash2 size={14} /> {deleteMut.isPending ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* List */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map(s => (
            <div key={s.id} className="group relative rounded-2xl border border-divider bg-surface p-5 shadow-sm hover:shadow-md transition-all duration-300">
              {/* Action buttons */}
              <div className="absolute right-4 top-4 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                <button
                  onClick={() => handleEdit(s)}
                  className="rounded-full bg-muted p-2 text-muted hover:text-primary hover:bg-primary/10 transition"
                  title="Editar"
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={() => setConfirmDelete(s)}
                  className="rounded-full bg-muted p-2 text-muted hover:text-red-600 hover:bg-red-50 transition"
                  title="Eliminar"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <div className="mb-3">
                <h3 className="text-base font-bold text-[color:var(--color-text)] truncate pr-16">{s.name}</h3>
                <p className="text-[10px] font-bold uppercase tracking-wider text-primary mt-0.5">{s.nit || 'Sin NIT'}</p>
              </div>

              <div className="space-y-2 text-xs text-muted">
                {s.contact_name && <div className="flex items-center gap-2 font-medium text-[color:var(--color-text)]"><User size={14} className="text-primary"/> {s.contact_name}</div>}
                {s.phone && <div className="flex items-center gap-2"><Phone size={14}/> {s.phone}</div>}
                {s.email && <div className="flex items-center gap-2"><Mail size={14}/> {s.email}</div>}
                {(s.city || s.address) && (
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="mt-0.5 shrink-0"/>
                    <span className="leading-tight">{s.city ? `${s.city}, ` : ''}{s.address}</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          {filtered.length === 0 && !isLoading && (
            <div className="col-span-full rounded-2xl border border-dashed border-divider p-12 text-center">
              <User size={48} className="mx-auto mb-4 text-muted opacity-20" />
              <p className="text-muted">No se encontraron proveedores</p>
              <button onClick={() => { reset(); setIsNew(true); }} className="mt-4 text-primary font-bold hover:underline">Crear el primero</button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default SuppliersPage;
